import type { ClientListItem } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useCallback, useMemo, useRef, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../../access/use-access"
import { clientsExportAvailable, fetchClients } from "../../../api/clients.api"
import { fetchEmployees } from "../../../api/directory.api"
import { formatTimeOfDay, todayInZone } from "../../../lib/format"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { cx } from "../../../lib/cx"
import { Badge, Button, Chip, Chips, StatusStripe, Tooltip } from "../../../ui"
import { CenterState, SearchField, StandHeader, TableSkeleton } from "../stand"
import styles from "../directories.module.css"
import { useResource } from "../use-resource"
import type { ClientsSearch } from "./clients-search"
import { ExportDialog } from "./export-dialog"
import { CLEARED_SEARCH, filtersOf } from "./clients-search"
import {
  DENIED,
  EMPTY_FILTER,
  EMPTY_FIRST_DAY,
  EXPORT_DENIED_REASON,
  clientPhoneLabel,
  clientsCountLabel,
  clientsFooterLabel,
  clientsListState,
  isNarrowed,
  threeMonthsBefore,
} from "./clients-state"

/**
 * Список клиентов (3-А1), семь состояний.
 *
 * ПЕРСОНАЛЬНЫЕ ДАННЫЕ — ГЛАВНОЕ, ЧТО ЗДЕСЬ ЕСТЬ, И ГЛАВНОЕ, ЧТО МОЖНО ИСПОРТИТЬ.
 *
 * 1. Телефон приходит уже погашенным: сервер отдаёт phone: null и оставляет маску.
 *    Экран не решает это заново и не «прячет лишнее» — прятать на клиенте то, что
 *    уже уехало в браузер, значит скрыть номер от глаз, но не от вкладки «Сеть».
 *
 * 2. «Выгрузить выборку» — НЕ кнопка «Экспорт» (ТЗ §3.5). Это отдельный сценарий:
 *    право только у владельца, повторное подтверждение вторым фактором, асинхронная
 *    подготовка файла, ссылка с ограниченным сроком, суточная квота 500 карточек.
 *    Ни остатка квоты, ни свежести второго фактора API не отдаёт, поэтому кнопка стоит
 *    выключенной с причиной — и это честнее, чем позволить нажать и упереться в отказ
 *    после того, как человек уже собрал выборку.
 */

/** Сколько строк просим у сервера за раз. Дальше — по мере прокрутки. */
const PAGE_SIZE = 50

export function ClientsScreen(props: {
  readonly search: ClientsSearch
  readonly onSearchChange: (patch: ClientsSearch) => void
  readonly onOpenClient: (clientId: string) => void
  readonly onOpenJournal: () => void
  readonly onOpenDuplicates: () => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const canView = useHasPermission("clients.list.view")
  const canOpenCard = useHasPermission("clients.card.view")
  const canCreate = useHasPermission("clients.create")
  const canExport = useHasPermission("clients.export")
  const canMerge = useHasPermission("clients.merge")
  const [exportOpen, setExportOpen] = useState(false)
  // Формируются ли файлы выгрузки вообще: пока хранилище не подключено — нет, и кнопка
  // говорит это до нажатия, а не после того, как человек написал цель выгрузки.
  const exportAvailability = useResource(
    canExport ? (signal) => clientsExportAvailable(signal) : null,
    canExport ? "clients-export-availability" : null,
  )
  const canBook = useHasPermission("appointments.create")

  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)

  const filters = filtersOf(props.search)
  const narrowed = isNarrowed(filters)

  const query = useMemo(
    () => ({
      search: filters.search.trim().length > 0 ? filters.search.trim() : undefined,
      limit: PAGE_SIZE,
      blacklistedOnly: filters.blockedOnly ? true : undefined,
      minNoShowCount: filters.noShowOnly ? 1 : undefined,
      lastVisitBefore: filters.staleOnly ? threeMonthsBefore(today) : undefined,
      lastVisitEmployeeId: filters.employeeId ?? undefined,
      sort: props.search.sort ?? "last_visit",
    }),
    [
      filters.search,
      filters.blockedOnly,
      filters.noShowOnly,
      filters.staleOnly,
      filters.employeeId,
      props.search.sort,
      today,
    ],
  )

  const key = canView ? JSON.stringify(query) : null
  const clients = useResource(
    canView ? (signal) => fetchClients(query, signal) : null,
    key,
  )

  // Мастера нужны одному чипу — «Мастер последнего визита». Загружаются отдельно
  // и молча: без них чип просто не предлагает выбора, а список работает.
  const employees = useResource(
    canView ? (signal) => fetchEmployees(false, signal) : null,
    canView ? "employees" : null,
  )

  /**
   * Размер базы — последнее известное число БЕЗ фильтров.
   *
   * Сервер отдаёт totalCount для текущего запроса, а макет обещает «Найдено 143
   * из 20 412». Второе число берётся отсюда: оно пришло с сервера, просто раньше.
   * Ref, а не состояние: перерисовка ради него не нужна, а обнуляться при каждом
   * вводе буквы в поиск оно не должно.
   */
  const baseCount = useRef<number | null>(null)
  if (!narrowed && clients.data?.totalCount !== undefined && clients.data.totalCount !== null) {
    baseCount.current = clients.data.totalCount
  }

  const state = clientsListState({
    canView,
    loading: clients.loading,
    problem: clients.problem,
    items: clients.data?.items ?? null,
    nextCursor: clients.data?.nextCursor ?? null,
    filters,
  })

  const toggle = useCallback(
    (patch: ClientsSearch) => {
      props.onSearchChange(patch)
    },
    [props],
  )

  if (state.kind === "denied") {
    return (
      <div className={styles.screen}>
        <StandHeader title="Клиенты" repeatsSection />
        <CenterState
          title={DENIED.title}
          text={DENIED.text}
          actions={
            <Button variant="primary" onClick={props.onOpenJournal}>
              {DENIED.action}
            </Button>
          }
        />
      </div>
    )
  }

  if (state.kind === "problem") {
    return <ProblemScreen problem={state.problem} onRetry={clients.reload} />
  }

  const offline = clients.problem !== null && clients.data !== null
  const staleTime =
    clients.loadedAt === null ? "" : formatTimeOfDay(clients.loadedAt, timeZone)

  const countLabel = clientsCountLabel({
    state,
    totalCount: clients.data?.totalCount ?? null,
    baseCount: baseCount.current,
    narrowed,
  })

  return (
    <div className={styles.screen}>
      {offline ? (
        <StatusStripe
          tone="offline"
          title="Нет связи"
          text={`Показан список на ${staleTime}, новые клиенты в нём могут отсутствовать. Создание клиента недоступно`}
          action={{ label: "Повторить", onClick: clients.reload }}
        />
      ) : null}

      <StandHeader title="Клиенты" count={countLabel} repeatsSection>
        <Button
          size="sm"
          deniedReason={canMerge ? undefined : "Объединять карточки может управляющий"}
          onClick={props.onOpenDuplicates}
        >
          Дубли
        </Button>
        <Button
          size="sm"
          deniedReason={
            !canExport
              ? EXPORT_DENIED_REASON
              : exportAvailability.data?.available === false
                ? "Выгрузка заработает, когда салону подключат хранилище файлов"
                : undefined
          }
          disabled={canExport && exportAvailability.data === null}
          onClick={() => setExportOpen(true)}
        >
          Выгрузить выборку
        </Button>
        <Button
          size="sm"
          variant="primary"
          deniedReason={
            offline
              ? "Пока нет связи, изменения недоступны — иначе клиент может потеряться"
              : canCreate
                ? undefined
                : "Заводить клиентов может администратор или управляющий"
          }
        >
          + Новый клиент
        </Button>
      </StandHeader>

      <div className={styles.toolbar}>
        <SearchField
          label="Поиск клиента"
          placeholder="Имя или часть номера — 4567, +7 916, 8 916…"
          value={props.search.q ?? ""}
          onChange={(value) => {
            toggle({ q: value.length === 0 ? undefined : value })
          }}
        />

        <Chips label="Фильтры списка клиентов">
          {/*
            Справочника меток салона API не отдаёт отдельным маршрутом, а собирать его
            из карточек на экране — значит предложить фильтр по тем меткам, что и так
            видны. Чип стоит выключенным с пояснением: прячут то, чего не будет никогда.
          */}
          <Tooltip text="Фильтр по меткам появится, когда салон сможет получить их список одним запросом">
            <Chip disabled>Метки ▾</Chip>
          </Tooltip>
          <Chip
            active={filters.staleOnly}
            onClick={() => {
              toggle({ stale: filters.staleOnly ? undefined : true })
            }}
          >
            Не были больше 3 месяцев
          </Chip>
          <Chip
            active={filters.noShowOnly}
            onClick={() => {
              toggle({ noshow: filters.noShowOnly ? undefined : true })
            }}
          >
            Неявки ▾
          </Chip>
          <Chip
            active={filters.blockedOnly}
            onClick={() => {
              toggle({ blocked: filters.blockedOnly ? undefined : true })
            }}
          >
            Ограничение записи
          </Chip>
        </Chips>

        <label className={styles.rowMeta}>
          <span className="plnr-visually-hidden">Мастер последнего визита</span>
          <select
            className={styles.input}
            value={filters.employeeId ?? ""}
            onChange={(event) => {
              const value = event.target.value
              toggle({ master: value.length === 0 ? undefined : value })
            }}
          >
            <option value="">Мастер последнего визита</option>
            {(employees.data ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.displayName}
              </option>
            ))}
          </select>
        </label>

        <span className={styles.toolbarEnd}>Сортировка: последний визит</span>
      </div>

      <div className={styles.body}>
        {state.kind === "empty_first_day" ? (
          <CenterState
            title={EMPTY_FIRST_DAY.title}
            text={EMPTY_FIRST_DAY.text}
            note={EMPTY_FIRST_DAY.note}
            actions={
              <>
                <Button variant="primary">Перенести базу</Button>
                <Button>Добавить клиента</Button>
              </>
            }
          />
        ) : state.kind === "empty_filter" ? (
          <CenterState
            title={
              state.search.length > 0
                ? `По запросу «${state.search}» ничего не нашли`
                : "По этим фильтрам никого нет"
            }
            text={EMPTY_FILTER.text}
            actions={
              <>
                <Button
                  onClick={() => {
                    props.onSearchChange(CLEARED_SEARCH)
                  }}
                >
                  Сбросить фильтры
                </Button>
                <Button variant="primary" deniedReason={canCreate ? undefined : "Заводить клиентов может администратор или управляющий"}>
                  Завести нового клиента
                </Button>
              </>
            }
          />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "36px" }}>
                    <span className="plnr-visually-hidden">Отметить</span>
                  </th>
                  <th>Клиент</th>
                  <th>Телефон</th>
                  <th>Метки</th>
                  <th>Последний визит</th>
                  <th className={styles.num}>Визиты</th>
                  <th className={styles.num}>Неявки</th>
                  <th />
                </tr>
              </thead>

              {state.kind === "loading" ? (
                <TableSkeleton columns={8} />
              ) : (
                <tbody>
                  {state.items.map((item) => (
                    <ClientRow
                      key={item.id}
                      item={item}
                      timeZone={timeZone}
                      canOpenCard={canOpenCard}
                      canBook={canBook && !offline}
                      onOpen={props.onOpenClient}
                    />
                  ))}
                </tbody>
              )}
            </table>

            {state.kind === "loading" ? null : (
              <div className={styles.tableFoot}>
                <span>
                  {clientsFooterLabel({
                    shown: state.items.length,
                    totalCount: clients.data?.totalCount ?? null,
                    hasMore: state.hasMore,
                  })}
                </span>
                <span>Отмечено: 0 · Поставить метку · Снять метку</span>
              </div>
            )}
          </div>
        )}
      </div>
      {exportOpen ? (
        <ExportDialog
          filter={{
            search: query.search,
            blacklistedOnly: query.blacklistedOnly,
            minNoShowCount: query.minNoShowCount,
            lastVisitBefore: query.lastVisitBefore,
            lastVisitEmployeeId: query.lastVisitEmployeeId,
            sort: query.sort,
          }}
          countLabel={countLabel}
          onClose={() => setExportOpen(false)}
        />
      ) : null}
    </div>
  )
}

/** «Сегодня, 13:00» / «12 сентября». Свежий визит важнее точной даты — он читается чаще. */
function lastVisitLabel(value: string | null, timeZone: string, today: string): string {
  if (value === null) return "—"
  const date = todayInZone(timeZone, new Date(value))
  if (date === today) return `Сегодня, ${formatTimeOfDay(value, timeZone)}`
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", timeZone }).format(
    new Date(value),
  )
}

function ClientRow(props: {
  readonly item: ClientListItem
  readonly timeZone: string
  readonly canOpenCard: boolean
  readonly canBook: boolean
  readonly onOpen: (clientId: string) => void
}): ReactNode {
  const { item } = props
  const phone = clientPhoneLabel(item)
  const today = todayInZone(props.timeZone)

  /*
   * У КАЖДОЙ ЯЧЕЙКИ СВОЁ ИМЯ (data-cell). На широком экране это строка таблицы, а на
   * телефоне (5-В5 — «узкая ширина списка 3-А1») та же строка раскладывается карточкой:
   * имя сверху, телефон и последний визит под ним, счётчики и «Записать» внизу.
   * Раскладывает CSS по этим именам, а не по номеру столбца: вставленный завтра столбец
   * не должен молча переставить поля карточки.
   */
  return (
    <tr className={styles.clientRow}>
      <td data-cell="select">
        <input type="checkbox" aria-label={`Отметить ${item.displayName}`} />
      </td>
      <td data-cell="name">
        {props.canOpenCard ? (
          <button type="button" className={styles.inlineLink} onClick={() => { props.onOpen(item.id) }}>
            {item.displayName}
          </button>
        ) : (
          <span className={styles.rowMain}>{item.displayName}</span>
        )}
        {item.isBlacklisted ? (
          <span className={styles.rowAlert}>Онлайн-запись ограничена</span>
        ) : null}
      </td>
      <td data-cell="phone" className={styles.nowrap} style={{ fontVariantNumeric: "tabular-nums" }}>
        {phone.text}
        {phone.masked ? <span className="plnr-visually-hidden"> номер скрыт правом доступа</span> : null}
      </td>
      <td data-cell="tags">
        <div className={styles.tagRow}>
          {item.tags.map((tag) => (
            <Badge key={tag.id} tone="gray">
              {tag.name}
            </Badge>
          ))}
        </div>
      </td>
      <td data-cell="visit" className={styles.nowrap}>
        {lastVisitLabel(item.lastVisitAt, props.timeZone, today)}
      </td>
      <td data-cell="visits" className={styles.num}>
        {item.visitsCount}
      </td>
      <td data-cell="noshow" className={cx(styles.num, item.noShowCount > 0 && styles.noShow)}>
        {item.noShowCount}
      </td>
      <td data-cell="action">
        <Button
          size="sm"
          deniedReason={props.canBook ? undefined : "Записывать клиентов может администратор — и только со связью"}
        >
          Записать
        </Button>
      </td>
    </tr>
  )
}
